import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';

// PrimeNG imports
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TextareaModule } from 'primeng/textarea';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { DatePickerModule } from 'primeng/datepicker';
import { MultiSelectModule } from 'primeng/multiselect';

// Models and services
import {
  ProcessedCommissionTransactionResponse,
  ProcessedCommissionTransactionDetailResponse,
  ProcessedCommissionTransactionResponsePagedResults,
  CreateStaffAdjustmentsRequest,
  StaffAdjustmentRequest,
  MarkCommissionsAsProcessedRequest,
  CommissionTransactionType
} from '../../models';
import { CommissionProcessedService } from '../../services';
import { CommissionPlanService } from '../../services/commission-plan.service';
import { CommonService, ToastMessageService } from '@fitness-force/api';
import { BreadcrumbComponent } from '@fitness-force/common/breadcrumb';
import { MultiSelectStaffComponent } from '@fitness-force/shared';
import { TENANTS_INFO } from '@fitness-force/state';
import type { TENANTSINTERFACE, STAFFINTERFACE } from '@fitness-force/models';
import { AppErrors } from '@fitness-force/models';
import { CurrencyformatPipe } from '@fitness-force/pipes/currency';
import { DateTimeFormatPipe } from '@fitness-force/pipes/datetime-format';
import { CommissionPlanResponse } from '../../models/commission-plan.model';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

@Component({
  selector: 'lib-commission-transaction',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    TableModule,
    ButtonModule,
    TagModule,
    DialogModule,
    ToastModule,
    TextareaModule,
    CheckboxModule,
    InputNumberModule, SelectModule,
    TooltipModule,
    DatePickerModule,
    MultiSelectModule,
    BreadcrumbComponent,
    MultiSelectStaffComponent,
    CurrencyformatPipe,
    DateTimeFormatPipe,
    NgClass
],
  providers: [MessageService],
  templateUrl: './commission-transaction.component.html',
  styleUrl: './commission-transaction.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommissionTransactionComponent implements OnInit {
  private commissionProcessedService = inject(CommissionProcessedService);
  private commissionPlanService = inject(CommissionPlanService);
  private toastMessageService = inject(ToastMessageService);
  private translateService = inject(TranslateService);
  private cdr = inject(ChangeDetectorRef);
  private store = inject(Store);
  private commonService = inject(CommonService);
  private fb = inject(UntypedFormBuilder);

  // Tab management
  activeTabIndex = 0;
  
  // Staff lists for each tab
  processStaffList: ProcessedCommissionTransactionResponse[] = [];
  processedStaffList: ProcessedCommissionTransactionResponse[] = [];
  
  // Expanded rows tracking
  expandedRows: { [key: string]: boolean } = {};
  
  // Loading states
  loading = false;
  
  // Pagination
  totalRecords = 0;
  currentPage = 1;
  pageSize = 10;
  
  // Selection
  selectedStaff: ProcessedCommissionTransactionResponse[] = [];
  selectedTransactions: Map<number, ProcessedCommissionTransactionDetailResponse[]> = new Map(); // staffId -> transactions
  
  // Dialogs
  showStaffAdjustmentDialog = false;
  showBulkTransactionProcessDialog = false;
  
  // Dialog data
  staffAdjustmentNotes = '';
  staffAdjustmentAmount: number | null = null;
  staffAdjustmentDate: Date = new Date();
  selectedStaffForAdjustment: STAFFINTERFACE[] = [];
  bulkTransactionProcessNotes = '';
  
  // Filter form and data
  filterForm: UntypedFormGroup;
  commissionPlans: CommissionPlanResponse[] = [];
  selectedPlanIds: number[] = [];
  selectedStaffIds: number[] = [];
  selectedTenantIds: number[] = [];
  dateFrom: Date | null = null;
  dateTo: Date | null = null;
  
  // Processing states
  processingStaffAdjustment = false;
  processingBulkTransactions = false;
  
  // Tenant info
  tenantId = 0;
  companyId = 0;
  currencyCode = 'USD';
  locale = 'en-US';
  // Enums for template
  CommissionTransactionType = CommissionTransactionType;


  constructor() {
    this.filterForm = this.fb.group({
      staffControl: [null]
    });
    
    // Set default dates to current month start and end
    const now = new Date();
    
    this.dateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
    this.dateTo = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  }

  ngOnInit(): void {
    this.store.select(TENANTS_INFO).subscribe((tenant: TENANTSINTERFACE) => {
      if (tenant?.selected_tenant?.id) {
        this.tenantId = tenant.selected_tenant?.id;
        this.companyId = tenant.selected_tenant?.company_id || 0;
        this.selectedTenantIds = [this.tenantId];
        this.loadCommissionPlans();
        this.loadTransactions();
      }
    });

    const currencyInfo = this.commonService.getCurrencyCodeAndLocale();
    this.currencyCode = currencyInfo.currencyCode || 'USD';
    this.locale = currencyInfo.locale || 'en-US';
  }

  loadCommissionPlans(): void {
    this.commissionPlanService.getTenantCommissionPlans(this.tenantId, {
      page: 1,
      page_size: 1000
    }).subscribe({
      next: (plans: CommissionPlanResponse[]) => {
        this.commissionPlans = plans || [];
        this.cdr.markForCheck();
      },
      error: (error: AppErrors) => {
        console.error('Error loading commission plans:', error);
      }
    });
  }

  loadTransactions(): void {
    this.loading = true;
    this.selectedStaff = [];
    this.cdr.markForCheck();

    // Call API to get transactions
    this.commissionProcessedService.getTenantProcessedTransactions(
      this.tenantId,
      {
        pageNumber: this.currentPage,
        pageSize: this.pageSize,
        dateFrom: this.dateFrom?.toISOString(),
        dateTo: this.dateTo?.toISOString(),
        staffIds: this.selectedStaffIds.length > 0 ? this.selectedStaffIds : undefined,
        planIds: this.selectedPlanIds.length > 0 ? this.selectedPlanIds : undefined,
        tenantIds: this.selectedTenantIds.length > 0 ? this.selectedTenantIds : undefined,
        transactionStatus: this.activeTabIndex === 0 ? 'Unprocessed' : 'Processed'
      }
    ).subscribe({
      next: (response: ProcessedCommissionTransactionResponsePagedResults) => {
        const allStaff = response.items || [];
        
        // Filter based on active tab
        if (this.activeTabIndex === 0) {
          // Process tab - transactions WITHOUT commProcessedId
          this.processStaffList = allStaff.map(staff => ({
            ...staff,
            transactions: staff.transactions?.filter(t => (t.comm_processed_id == 0 || t.comm_processed_id == null)) || []
          })).filter(staff => staff.transactions && staff.transactions.length > 0);
        } else {
          // Processed tab - transactions WITH commProcessedId
          this.processedStaffList = allStaff.map(staff => ({
            ...staff,
            transactions: staff.transactions?.filter(t => (t.comm_processed_id != 0 || t.comm_processed_id != null)) || []
          })).filter(staff => staff.transactions && staff.transactions.length > 0);
        }
        
        this.totalRecords = response.total_items || 0;
        this.currentPage = response.current_page || 1;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: (error: AppErrors) => {
        console.error('Error loading commission transactions:', error);
        this.toastMessageService.showError(error);
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  onTabChange(event: any): void {
    this.activeTabIndex = event.index;
    this.currentPage = 1;
    this.selectedStaff = [];
    this.selectedTransactions.clear();
    this.expandedRows = {};
    this.loadTransactions();
  }

  onPageChange(event: any): void {
    this.currentPage = event.page + 1;
    this.pageSize = event.rows;
    this.loadTransactions();
  }

  onRowExpand(event: any): void {
    this.expandedRows[event.data.staff_id] = true;
    this.cdr.markForCheck();
  }

  onRowCollapse(event: any): void {
    delete this.expandedRows[event.data.staff_id];
    this.cdr.markForCheck();
  }

  expandAll(): void {
    this.expandedRows = {};
    const currentList = this.activeTabIndex === 0 ? this.processStaffList : this.processedStaffList;
    currentList.forEach(staff => {
      this.expandedRows[staff.staff_id] = true;
    });
    this.cdr.markForCheck();
  }

  collapseAll(): void {
    this.expandedRows = {};
    this.cdr.markForCheck();
  }

  populateStaffOptions(): void {
    // Not needed anymore since we're using multi-select-staff component
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadTransactions();
  }

  clearFilters(): void {
    this.selectedStaffIds = [];
    this.selectedPlanIds = [];
    const now = new Date();
    this.dateFrom = new Date(now.getFullYear(), now.getMonth(), 1);
    this.dateTo = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    this.currentPage = 1;
    this.loadTransactions();
  }

  onStaffFilterChanged(staffs: STAFFINTERFACE[]): void {
    this.selectedStaffIds = staffs.map(s => s.id);
    this.cdr.markForCheck();
  }

  onAdjustmentStaffChanged(staffs: STAFFINTERFACE[]): void {
    this.selectedStaffForAdjustment = staffs;
    this.cdr.markForCheck();
  }

  get currentStaffList(): ProcessedCommissionTransactionResponse[] {
    return this.activeTabIndex === 0 ? this.processStaffList : this.processedStaffList;
  }

  get selectedStaffIdsForAdjustment(): number[] {
    return this.selectedStaffForAdjustment.map(s => s.id);
  }

  get hasSelectedStaff(): boolean {
    return this.selectedStaff && this.selectedStaff.length > 0;
  }

  get hasSelectedTransactions(): boolean {
    return this.selectedTransactions.size > 0 && 
           Array.from(this.selectedTransactions.values()).some(txns => txns.length > 0);
  }

  get totalSelectedTransactions(): number {
    return Array.from(this.selectedTransactions.values())
      .reduce((total, txns) => total + txns.length, 0);
  }

  // Check if a staff member is selected (based on staff_id and type_id combination)
  isStaffSelected(staff: ProcessedCommissionTransactionResponse): boolean {
    if (!staff.transactions || staff.transactions.length === 0) return false;
    
    const unprocessedTransactions = staff.transactions.filter(t => !t.comm_processed_id);
    if (unprocessedTransactions.length === 0) return false;
    
    const staffTransactions = this.selectedTransactions.get(staff.staff_id);
    if (!staffTransactions || staffTransactions.length === 0) return false;
    
    // Check if all unprocessed transactions for this staff_id and type_id are selected
    return unprocessedTransactions.every(txn => 
      staffTransactions.some(selected => selected.id === txn.id && selected.type_id === txn.type_id)
    );
  }

  // Handle staff-level checkbox selection (based on staff_id and type_id)
  onStaffSelect(staff: ProcessedCommissionTransactionResponse, checked: boolean): void {
    const unprocessedTransactions = staff.transactions?.filter(t => !t.comm_processed_id) || [];
    
    if (checked) {
      // Select all unprocessed transactions for this staff_id and type_id combination
      const existingTransactions = this.selectedTransactions.get(staff.staff_id) || [];
      
      // Remove any existing transactions for this type_id to avoid duplicates
      const filteredExisting = existingTransactions.filter(t => t.type_id !== staff.type_id);
      
      // Add all unprocessed transactions for this staff_id and type_id
      const updatedTransactions = [...filteredExisting, ...unprocessedTransactions];
      this.selectedTransactions.set(staff.staff_id, updatedTransactions);
    } else {
      // Deselect all transactions for this staff_id and type_id combination
      const existingTransactions = this.selectedTransactions.get(staff.staff_id) || [];
      const remainingTransactions = existingTransactions.filter(t => t.type_id !== staff.type_id);
      
      if (remainingTransactions.length > 0) {
        this.selectedTransactions.set(staff.staff_id, remainingTransactions);
      } else {
        this.selectedTransactions.delete(staff.staff_id);
      }
    }
    
    this.syncStaffSelection();
    this.cdr.markForCheck();
  }

  // Check if all staff are selected
  areAllStaffSelected(): boolean {
    const currentList = this.currentStaffList;
    if (currentList.length === 0) return false;
    
    return currentList.every(staff => this.isStaffSelected(staff));
  }

  // Handle header "select all" checkbox
  onSelectAllStaff(checked: boolean): void {
    const currentList = this.currentStaffList;
    
    if (checked) {
      // Select all staff and all their unprocessed transactions (grouped by staff_id and type_id)
      currentList.forEach(staff => {
        const unprocessedTransactions = staff.transactions?.filter(t => !t.comm_processed_id) || [];
        if (unprocessedTransactions.length > 0) {
          const existingTransactions = this.selectedTransactions.get(staff.staff_id) || [];
          
          // Remove any existing transactions for this type_id to avoid duplicates
          const filteredExisting = existingTransactions.filter(t => t.type_id !== staff.type_id);
          
          // Add all unprocessed transactions for this staff_id and type_id
          const updatedTransactions = [...filteredExisting, ...unprocessedTransactions];
          this.selectedTransactions.set(staff.staff_id, updatedTransactions);
        }
      });
    } else {
      // Deselect all
      this.selectedTransactions.clear();
    }
    
    this.syncStaffSelection();
    this.cdr.markForCheck();
  }

  // Sync staff selection array based on transaction selections
  private syncStaffSelection(): void {
    const currentList = this.currentStaffList;
    this.selectedStaff = currentList.filter(staff => this.isStaffSelected(staff));
  }

  // Transaction selection methods
  isTransactionSelected(staffId: number, transaction: ProcessedCommissionTransactionDetailResponse): boolean {
    const staffTransactions = this.selectedTransactions.get(staffId);
    return staffTransactions ? staffTransactions.some(t => t.id === transaction.id) : false;
  }

  onTransactionSelect(staffId: number, transaction: ProcessedCommissionTransactionDetailResponse, checked: boolean): void {
    const staffTransactions = this.selectedTransactions.get(staffId) || [];
    
    if (checked) {
      // Select - create new array with this transaction added (if not already present)
      const alreadyExists = staffTransactions.some(t => t.id === transaction.id);
      if (!alreadyExists) {
        const updatedTransactions = [...staffTransactions, transaction];
        this.selectedTransactions.set(staffId, updatedTransactions);
      }
    } else {
      // Deselect - create new array without this transaction
      const updatedTransactions = staffTransactions.filter(t => t.id !== transaction.id);
      if (updatedTransactions.length === 0) {
        this.selectedTransactions.delete(staffId);
      } else {
        this.selectedTransactions.set(staffId, updatedTransactions);
      }
    }
    
    // Sync staff selection after transaction selection changes
    this.syncStaffSelection();
    this.cdr.markForCheck();
  }

  areAllTransactionsSelected(staff: ProcessedCommissionTransactionResponse): boolean {
    if (!staff.transactions || staff.transactions.length === 0) return false;
    const unprocessedTransactions = staff.transactions.filter(t => !t.comm_processed_id);
    if (unprocessedTransactions.length === 0) return false;
    
    const staffTransactions = this.selectedTransactions.get(staff.staff_id);
    if (!staffTransactions || staffTransactions.length === 0) return false;
    
    // Check if all unprocessed transactions for this staff_id and type_id are selected
    return unprocessedTransactions.every(txn => 
      staffTransactions.some(selected => selected.id === txn.id && selected.type_id === txn.type_id)
    );
  }

  onSelectAllTransactions(staff: ProcessedCommissionTransactionResponse, checked: boolean): void {
    const unprocessedTransactions = staff.transactions?.filter(t => !t.comm_processed_id) || [];
    
    if (checked) {
      // Select all unprocessed transactions for this staff_id and type_id combination
      const existingTransactions = this.selectedTransactions.get(staff.staff_id) || [];
      
      // Remove any existing transactions for this type_id to avoid duplicates
      const filteredExisting = existingTransactions.filter(t => t.type_id !== staff.type_id);
      
      // Add all unprocessed transactions for this staff_id and type_id
      const updatedTransactions = [...filteredExisting, ...unprocessedTransactions];
      this.selectedTransactions.set(staff.staff_id, updatedTransactions);
    } else {
      // Deselect all transactions for this staff_id and type_id combination
      const existingTransactions = this.selectedTransactions.get(staff.staff_id) || [];
      const remainingTransactions = existingTransactions.filter(t => t.type_id !== staff.type_id);
      
      if (remainingTransactions.length > 0) {
        this.selectedTransactions.set(staff.staff_id, remainingTransactions);
      } else {
        this.selectedTransactions.delete(staff.staff_id);
      }
    }
    
    // Sync staff selection after transaction selection changes
    this.syncStaffSelection();
    this.cdr.markForCheck();
  }

  // Staff Adjustment Dialog
  openStaffAdjustmentDialog(): void {
    this.staffAdjustmentNotes = '';
    this.staffAdjustmentAmount = null;
    this.staffAdjustmentDate = new Date();
    this.selectedStaffForAdjustment = [];
    this.showStaffAdjustmentDialog = true;
    this.cdr.markForCheck();
  }

  closeStaffAdjustmentDialog(): void {
    this.showStaffAdjustmentDialog = false;
    this.staffAdjustmentNotes = '';
    this.staffAdjustmentAmount = null;
    this.staffAdjustmentDate = new Date();
    this.selectedStaffForAdjustment = [];
    this.cdr.markForCheck();
  }

  submitStaffAdjustment(): void {
    if (!this.selectedStaffForAdjustment || this.selectedStaffForAdjustment.length === 0) {
      this.toastMessageService.showError(
        this.translateService.instant('COMMISSION_MANAGEMENT.TRANSACTIONS.STAFF_REQUIRED')
      );
      return;
    }

    if (!this.staffAdjustmentDate) {
      this.toastMessageService.showError(
        this.translateService.instant('COMMISSION_MANAGEMENT.TRANSACTIONS.DATE_REQUIRED')
      );
      return;
    }

    if (!this.staffAdjustmentAmount || this.staffAdjustmentAmount <= 0) {
      this.toastMessageService.showError(
        this.translateService.instant('COMMISSION_MANAGEMENT.TRANSACTIONS.AMOUNT_REQUIRED')
      );
      return;
    }

    if (!this.staffAdjustmentNotes.trim()) {
      this.toastMessageService.showError(
        this.translateService.instant('COMMISSION_MANAGEMENT.TRANSACTIONS.NOTES_REQUIRED')
      );
      return;
    }

    this.processingStaffAdjustment = true;
    this.cdr.markForCheck();

    // Convert amount to minor units (cents)
    const amountInMinor = Math.round(this.staffAdjustmentAmount * 100);
    
    // Convert date to UTC ISO string
    // Create a new date at midnight UTC to avoid timezone issues
    const adjustmentDate = new Date(Date.UTC(
      this.staffAdjustmentDate.getFullYear(),
      this.staffAdjustmentDate.getMonth(),
      this.staffAdjustmentDate.getDate(),
      0, 0, 0, 0
    )).toISOString();

    // Build staff adjustments array for all selected staff
    const staffAdjustments: StaffAdjustmentRequest[] = this.selectedStaffForAdjustment.map(staff => ({
      staff_id: staff.id,
      adjutment_date: adjustmentDate,
      adjustment_amount_in_minor: amountInMinor,
      adjustment_notes: this.staffAdjustmentNotes
    }));

    const request: CreateStaffAdjustmentsRequest = {
      staff_adjutments: staffAdjustments
    };

    this.commissionProcessedService.createStaffAdjustments(this.tenantId, request).subscribe({
      next: () => {
        this.toastMessageService.showSuccess(
          this.translateService.instant('COMMISSION_MANAGEMENT.TRANSACTIONS.STAFF_ADJUSTMENT_SUCCESS')
        );
        this.closeStaffAdjustmentDialog();
        this.processingStaffAdjustment = false;
        this.loadTransactions();
        this.cdr.markForCheck();
      },
      error: (error: AppErrors) => {
        this.toastMessageService.showError(error);
        this.processingStaffAdjustment = false;
        this.cdr.markForCheck();
      }
    });
  }


  // Bulk Transaction Process Dialog
  openBulkTransactionProcessDialog(): void {
    if (!this.hasSelectedTransactions) {
      return;
    }
    this.bulkTransactionProcessNotes = '';
    this.showBulkTransactionProcessDialog = true;
    this.cdr.markForCheck();
  }

  closeBulkTransactionProcessDialog(): void {
    this.showBulkTransactionProcessDialog = false;
    this.bulkTransactionProcessNotes = '';
    this.cdr.markForCheck();
  }

  submitBulkTransactionProcess(): void {
    if (!this.hasSelectedTransactions || !this.bulkTransactionProcessNotes.trim()) {
      this.toastMessageService.showError(
        this.translateService.instant('COMMISSION_MANAGEMENT.TRANSACTIONS.NOTES_REQUIRED')
      );
      return;
    }

    this.processingBulkTransactions = true;
    this.cdr.markForCheck();

    // Build commission transaction array with IDs and notes
    const commissionTransactionIds: number[] = [];
    this.selectedTransactions.forEach((transactions) => {
      transactions.forEach(txn => {
        commissionTransactionIds.push(
         txn.id,
        );
      });
    });

    const request: MarkCommissionsAsProcessedRequest = {
      transaction_ids: commissionTransactionIds,
      payroll_transaction_id:"",
      notes: this.bulkTransactionProcessNotes
    };

    this.commissionProcessedService.markCommissionsAsProcessed(this.tenantId, request).subscribe({
      next: () => {
        this.toastMessageService.showSuccess(
          this.translateService.instant('COMMISSION_MANAGEMENT.TRANSACTIONS.BULK_TRANSACTION_PROCESSED_SUCCESS')
        );
        this.closeBulkTransactionProcessDialog();
        this.processingBulkTransactions = false;
        this.selectedTransactions.clear();
        this.loadTransactions();
        this.cdr.markForCheck();
      },
      error: (error: AppErrors) => {
        this.toastMessageService.showError(error);
        this.processingBulkTransactions = false;
        this.cdr.markForCheck();
      }
    });
  }

  // Utility methods
  getTransactionTypeSeverity(type: CommissionTransactionType): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch (type) {
      case CommissionTransactionType.Membership:
        return 'success';
      case CommissionTransactionType.Service:
        return 'info';
      case CommissionTransactionType.Product:
        return 'warn';
      case CommissionTransactionType.Package:
        return 'secondary';
      default:
        return 'contrast';
    }
  }

  hasTransactions(staff: ProcessedCommissionTransactionResponse): boolean {
    return staff.transactions !== null && staff.transactions !== undefined && staff.transactions.length > 0;
  }

  getTransactionsCount(staff: ProcessedCommissionTransactionResponse): string {
    const count = staff.transactions?.length || 0;
    return count === 1 
      ? `${count} ${this.translateService.instant('COMMISSION_MANAGEMENT.TRANSACTIONS.TRANSACTION')}` 
      : `${count} ${this.translateService.instant('COMMISSION_MANAGEMENT.TRANSACTIONS.TRANSACTIONS')}`;
  }


}

